/**
 * useAgentSelection Hook
 * 
 * Specialized hook for agent selection with hostname filtering ONLY
 * Built on top of useEntitySelection with agent-specific configurations
 * When using hostname search, other filters are ignored to provide
 * focused hostname-based filtering.
 * 
 * @example
 * const agentSelection = useAgentSelection();
 * 
 * // Use in component:
 * <input 
 *   placeholder={agentSelection.searchPlaceholder}
 *   value={agentSelection.searchTerm}
 *   onChange={(e) => agentSelection.handleSearch(e.target.value)}
 * />
 */

import { useEntitySelection } from './useEntitySelection';
import { agentService } from '@/services';
import type { Agent } from '@/types';

interface UseAgentSelectionOptions {
  initialPageSize?: number;
  queryKeySuffix?: string; // For multiple agent selectors on the same page
}

interface UseAgentSelectionReturn extends ReturnType<typeof useEntitySelection<Agent>> {
  // Agent-specific methods
  selectOnlineAgents: () => void;
  selectByStatus: (status: Agent['status']) => void;
  getSelectedAgents: () => Agent[];
}

/**
 * Hook for agent selection with hostname filtering ONLY
 */
export function useAgentSelection({
  initialPageSize = 10,
  queryKeySuffix = '',
}: UseAgentSelectionOptions = {}): UseAgentSelectionReturn {
  
  const entitySelection = useEntitySelection<Agent>({
    fetchFn: agentService.getAgents,
    queryKey: `agents-selection${queryKeySuffix ? `-${queryKeySuffix}` : ''}`,
    initialPageSize,
    // No filters parameter - hostname filtering only
    searchField: 'hostname',
    searchPlaceholder: 'Buscar por hostname...',
  });

  /**
   * Select all online agents
   */
  const selectOnlineAgents = () => {
    const onlineAgentIds = entitySelection.entities
      .filter(agent => agent.status === 'online')
      .map(agent => agent.id);
    
    entitySelection.setSelectedIds(onlineAgentIds);
  };

  /**
   * Select agents by status
   */
  const selectByStatus = (status: Agent['status']) => {
    const agentIds = entitySelection.entities
      .filter(agent => agent.status === status)
      .map(agent => agent.id);
    
    entitySelection.setSelectedIds(agentIds);
  };

  /**
   * Get selected agent entities (not just IDs)
   */
  const getSelectedAgents = (): Agent[] => {
    return entitySelection.entities.filter(agent => 
      entitySelection.selectedIds.includes(agent.id)
    );
  };

  return {
    ...entitySelection,
    selectOnlineAgents,
    selectByStatus,
    getSelectedAgents,
  };
}

export default useAgentSelection;